/**
 * Status Constants
 *
 * DO NOT use hardcoded status IDs in your code!
 * Always use status names for comparisons as they are consistent across environments.
 *
 * Status IDs can differ between:
 * - ZenAPI and Strapi backends
 * - Development and production databases
 * - After database migrations or resets
 *
 * Usage:
 *   import { STATUS_NAMES, isAwaitingStatus } from '@/constants/statusConstants';
 *
 *   if (status.name === STATUS_NAMES.AWAITING_OFFER_ACCEPTANCE) { ... }
 *   if (isAwaitingStatus(status)) { ... }
 */

// Status names - these are consistent across environments
export const STATUS_NAMES = {
  // Loan Application Statuses
  AWAITING_VERIFICATION: 'Awaiting Verification',
  VERIFIED: 'Verified',
  NOT_VERIFIED: 'Not Verified',
  REJECTED: 'Rejected',

  // Offer Statuses
  AWAITING_OFFER: 'Awaiting Offer',
  AWAITING_OFFER_ACCEPTANCE: 'Awaiting offer acceptance',
  FINANCING_OFFER_SENT: 'Financing offer sent',
  FINANCING_OFFER_ACCEPTED: 'Financing offer accepted',
  FINANCING_OFFER_NOT_ACCEPTED: 'Financing offer not accepted',
  FINANCING_OFFERS_REJECTED: 'Financing offers received but rejected',

  // Agreement Statuses
  AWAITING_SIGNATURE: 'Awaiting Signature',
  LOAN_AGREEMENT_SIGNED: 'Loan Agreement Document Signed',

  // Funding Statuses
  FUNDS_PAID_OUT: 'Funds paid out by financier',
  FUND_ISSUED: 'Fund Issued',

  // Repayment Statuses
  LOAN_REPAID: 'Loan Repaid',
  OVERDUE_LOAN: 'Overdue Loan',
  LOAN_OVERDUE: 'Loan Overdue',
  LOAN_DEFAULT: 'Loan Default',

  // Profile Statuses
  COMPLETED: 'Completed'
};

// SME-facing status titles (these may differ from internal status names)
export const STATUS_TITLES = {
  AWAITING_VERIFICATION: 'Awaiting Verification',
  VERIFIED: 'Verified',
  NOT_VERIFIED: 'Not Verified',
  REJECTED: 'Rejected',
  AWAITING_OFFER: 'Awaiting Offer',
  AWAITING_OFFER_ACCEPTANCE: 'Awaiting offer acceptance',
  // Add more as needed
};

/**
 * Helper function to check if a status is an "awaiting" status
 * This replaces hardcoded ID checks like:
 *   status.id === '5ec3dfc47db6ab2ca11d07b6' || status.id === '5d9d9fcb875e3cbf48258855'
 *
 * @param {Object} status - Status object with name or smeTitle
 * @returns {boolean}
 */
export const isAwaitingStatus = (status) => {
  if (!status) return false;

  const statusName = status.smeTitle || status.name;

  return statusName === STATUS_NAMES.AWAITING_OFFER_ACCEPTANCE ||
         statusName === STATUS_NAMES.AWAITING_OFFER;
};

/**
 * Helper function to check if a status matches a specific status name
 * @param {Object} status - Status object
 * @param {string} statusName - Status name to compare
 * @returns {boolean}
 */
export const isStatus = (status, statusName) => {
  if (!status || !statusName) return false;
  const name = status.smeTitle || status.name;
  return name === statusName;
};

/**
 * Get status name from status object (handles both smeTitle and name)
 * @param {Object} status - Status object
 * @returns {string|null}
 */
export const getStatusName = (status) => {
  if (!status) return null;
  return status.smeTitle || status.name || null;
};

/**
 * Check if offer status is accepted
 * @param {string} offerStatus - Offer status string
 * @returns {boolean}
 */
export const isOfferAccepted = (offerStatus) => {
  return offerStatus === 'accepted';
};

/**
 * Status messages for user display
 */
export const STATUS_MESSAGES = {
  [STATUS_NAMES.COMPLETED]: 'Your business profile is completed you can now apply for loan(s) whilst your profile is being verified',
  [STATUS_NAMES.VERIFIED]: 'Your loan has been verified and has been shared with our network of financiers',
  [STATUS_NAMES.NOT_VERIFIED]: 'Your loan has not been verified. You will receive an email from the Nisa Team indicating what needs to be corrected.',
  [STATUS_NAMES.REJECTED]: 'Your loan has been rejected by the Nisa Team. Look out for an email detailing your rejection status.',
  [STATUS_NAMES.AWAITING_OFFER]: 'Your loan has been shared with our network of financiers. Please be patient whilst we await an offer(s).',
  [STATUS_NAMES.AWAITING_VERIFICATION]: 'Your loan has been submitted and is currently being reviewed by the Nisa Team.',
  [STATUS_NAMES.AWAITING_OFFER_ACCEPTANCE]: 'You have received an offer from a financier and are awaiting your response.',
  [STATUS_NAMES.FINANCING_OFFER_ACCEPTED]: 'You have accepted the loan offer. Look out for an email with the loan agreement.',
  [STATUS_NAMES.FUND_ISSUED]: 'Funds have been issued',
  [STATUS_NAMES.LOAN_REPAID]: 'The loan amount has been repaid',
  [STATUS_NAMES.FUNDS_PAID_OUT]: 'The loan amount has been paid out',
  [STATUS_NAMES.LOAN_DEFAULT]: 'This loan has been marked as having payment defaults. The Nisa Team will reach out to try and rectify this situation',
  [STATUS_NAMES.AWAITING_SIGNATURE]: 'A loan agreement has been sent out. Please check your emails.',
  [STATUS_NAMES.FINANCING_OFFER_NOT_ACCEPTED]: 'The offer has not been accepted',
  [STATUS_NAMES.FINANCING_OFFERS_REJECTED]: 'The loan offer has been rejected',
  [STATUS_NAMES.OVERDUE_LOAN]: 'This loan has been marked as being overdue.',
  [STATUS_NAMES.LOAN_OVERDUE]: 'Your loan has been marked as overdue. Please stay up to date with your payments.',
  [STATUS_NAMES.FINANCING_OFFER_SENT]: 'The offer has been successfully sent and is awaiting a response.',
  [STATUS_NAMES.LOAN_AGREEMENT_SIGNED]: 'The loan agreement has been successfully signed. Funds can now be issued.'
};

/**
 * Get status message for display
 * @param {Object|string} status - Status object or status name
 * @returns {string}
 */
export const getStatusMessage = (status) => {
  const statusName = typeof status === 'string' ? status : getStatusName(status);
  return STATUS_MESSAGES[statusName] || '';
};
